/**
 * LEAF RETIREMENT — the UI half of the CEO ruling of 2026-09-07 ("leaf category rows must be
 * DELETED, not preserved", super firm).
 *
 * ⛔⛔ WHY A PANEL AND NOT JUST A ROUTE. Measured 2026-09-08: `routes/category-characteristics.js`
 * carried ZERO `router.delete` routes and this admin page had no delete control for a category, so
 * the ruling was unexecutable by anyone — including the person who made it. The standing UI-ONLY
 * rule in this codebase says a capability with no button is not delivered; a maintenance verb that
 * only an agent with a shell can reach is exactly that.
 *
 * ⛔ ONE ROW PER CLICK, DELIBERATELY, AND THERE IS NO "RETIRE ALL". The standing rule is
 * smoke-test one record before any bulk job over 50, and 1,216 of 2,313 rows fleet-wide are bare
 * leaves. A bulk button would let an operator satisfy that rule's letter in a single click. The
 * preview is what makes the single-row verb tolerable: the whole job, and every refusal in it, is
 * visible before anything is deleted.
 *
 * ⚠️ THE PREVIEW IS A SNAPSHOT AND THE PANEL SAYS SO. It is bound to the sector it was taken
 * against and is dropped the moment the sector changes — a verdict computed against sector 72 must
 * never be actionable while sector 84 is on screen. Within a sector it can still go stale (another
 * operator researches the full path); the server re-runs the identical verdict inside the deleting
 * transaction, so a stale DELETABLE is refused there rather than acted on here.
 */
import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Trash2, ShieldAlert, Scissors } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

/**
 * Verdicts, per the CEO rulings of 2026-09-08 as relayed by orchestrator:
 *   DELETABLE — full-path twin exists; leaf-only personas are ACCEPTED LOSS (enumerated in
 *               `discarded_personas`); non-persona fields are merged into the twin first.
 *   REFUSED   — more than one full path claims the leaf (later writes would fail as ambiguous),
 *               or an object field is present on BOTH rows with different content (nobody has
 *               ruled on which is right; refusing loses nothing).
 *   NO_TWIN   — no full-path row at all. The leaf is the ONLY copy. Not covered by any ruling;
 *               hard refusal with no override, on 834 rows fleet-wide.
 */
export type LeafVerdict = 'DELETABLE' | 'REFUSED' | 'NO_TWIN';

export interface DiscardedPersona { persona_key: string; reason: 'only_on_leaf' | 'weights_differ' | 'weighted_only_on_leaf' | 'unidentifiable' }

export interface LeafRetirementRow {
  /** Set client-side after a successful retirement: the row stays on screen with its outcome, no button. */
  retired?: boolean;
  category_path: string;
  times_used: number | null;
  status: string | null;
  personas: number;
  weighted_personas: number;
  full_path: string | null;
  full_path_candidates?: number;
  full_path_personas: number;
  full_path_weighted: number;
  verdict: LeafVerdict;
  reason_code?: string | null;
  because: string;
  discarded_personas: DiscardedPersona[];
  merge: {
    conflicts: string[];
    /** Object fields both rows hold with different content: the full path's value stands, the leaf's is dropped (CEO 2026-09-09). */
    discarded_fields?: string[];
    arrays: Record<string, number>;
    objects: Record<string, string>;
    provenance_from_leaf: boolean;
    fields: string[];
  } | null;
}

interface PreviewState {
  sectorId: number;
  rows: LeafRetirementRow[];
  deletable: number;
  refused: number;
  noTwin: number;
  personasToDiscard: number;
}

export interface LeafRetirementPanelProps {
  sectorId: number | null;
  /** Called after a row is actually deleted, so the page's category list stops showing it. */
  onRetired?: () => void;
}

export function LeafRetirementPanel({ sectorId, onRetired }: LeafRetirementPanelProps) {
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Path awaiting a second click. A destructive verb gets two clicks, never a silent one. */
  const [confirming, setConfirming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  /** Per-row outcome: what the server said, kept verbatim. */
  const [outcome, setOutcome] = useState<Record<string, { ok: boolean; message: string }>>({});
  /**
   * ⛔⛔ EVERY PREVIEW HAS A GENERATION, AND A DELETE RESPONSE MAY ONLY TOUCH THE GENERATION IT WAS
   * FIRED FROM (both codex legs, minor, 2026-09-08). Without this: start deleting `Backpacks` in
   * sector 72, switch to 84, preview — and when the 72 response lands it removes sector 84's
   * `Backpacks` from the list and decrements 84's count, for a deletion that happened in 72. The
   * generation is captured at fire time and compared in the continuation; a stale response is
   * dropped on the floor (it already succeeded or failed server-side — the parent's reload shows it).
   */
  const generationRef = useRef(0);

  // ⛔ DERIVED ON EVERY RENDER, NEVER STORED. A preview taken against another sector must stop
  // being actionable on the first render after the switch, not after an effect gets a turn.
  const inScope = preview !== null && sectorId !== null && preview.sectorId === sectorId;

  async function runPreview() {
    if (sectorId === null) return;
    const firedAt = sectorId;
    const gen = ++generationRef.current;
    setLoading(true);
    setError(null);
    setPreview(null);
    setOutcome({});
    setConfirming(null);
    try {
      const res = await authFetch(`${API_URL}/category-characteristics/${firedAt}/leaf-retirement/preview`);
      const body = await res.json().catch(() => null);
      if (!res.ok || !body?.success) {
        // ⛔ NO MOCK, NO DEFAULT. The server's own message is the useful part of a failure.
        throw new Error(body?.error || `Preview failed with HTTP ${res.status}`);
      }
      if (generationRef.current !== gen) return; // a newer preview was fired while this one was in flight
      setPreview({
        sectorId: firedAt,
        rows: body.rows || [],
        deletable: body.deletable ?? 0,
        refused: body.refused ?? 0,
        noTwin: body.no_twin ?? 0,
        personasToDiscard: body.personas_to_discard ?? 0,
      });
    } catch (e: any) {
      if (generationRef.current === gen) setError(e?.message || String(e));
    } finally {
      if (generationRef.current === gen) setLoading(false);
    }
  }

  async function retire(row: LeafRetirementRow) {
    if (sectorId === null || !inScope) return;
    // Captured at fire time. The continuation below compares against them and never reads the
    // live props/state — those may belong to a different sector by the time the response lands.
    const firedSector = sectorId;
    const gen = generationRef.current;
    setDeleting(row.category_path);
    setConfirming(null);
    try {
      const res = await authFetch(
        `${API_URL}/category-characteristics/${firedSector}/category/${encodeURIComponent(row.category_path)}/leaf`,
        { method: 'DELETE' },
      );
      const body = await res.json().catch(() => null);
      if (generationRef.current !== gen) return; // the preview this row came from is gone
      if (!res.ok || !body?.deleted) {
        // A 409 here is the server re-running the verdict inside the transaction and disagreeing
        // with the snapshot on screen. That is the guard working; show what it said.
        setOutcome((o) => ({
          ...o,
          [row.category_path]: { ok: false, message: body?.error || `Delete failed with HTTP ${res.status}` },
        }));
        return;
      }
      const discarded: DiscardedPersona[] = body.discarded_personas || [];
      const mergedFields: string[] = body.merged?.fields || [];
      // ⛔ THE DROPPED FIELDS COME FROM THE DELETE RESPONSE, NEVER FROM THE PREVIEW ROW (codex, both legs
      // @bce9197bd, same file:line). Preview and delete are separate requests and research writes to these
      // rows between clicks; a field that became conflicting in between is dropped and recorded by the
      // server, and this message is the only thing the operator reads about it. A message built from the
      // preview snapshot would make the operator's record and the audit stamp disagree without anyone
      // noticing. An absent array is said out loud rather than read as "nothing dropped".
      const droppedFields: string[] | null = Array.isArray(body.discarded_object_fields) ? body.discarded_object_fields : null;
      setOutcome((o) => ({
        ...o,
        [row.category_path]: {
          ok: true,
          // ⛔ SAY WHAT WAS LOST, NOT "RETAINED". Jaume accepted the loss; he did not ask for it to be silent.
          message: `Retired into "${body.full_path_retained?.category_path}". `
            + (discarded.length > 0
              ? `Discarded ${discarded.length} persona(s): ${discarded.map((d) => d.persona_key).join(', ')}. `
              : 'No personas discarded. ')
            + (droppedFields === null
              ? 'The server did not report which leaf fields were dropped. '
              : droppedFields.length > 0
                ? `Dropped ${droppedFields.length} leaf field(s), full path wins: ${droppedFields.join(', ')}. `
                : 'No leaf fields dropped. ')
            + (mergedFields.length > 0 ? `Merged ${mergedFields.join(', ')} into it first.` : 'Nothing to merge.'),
        },
      }));
      // ⛔ THE ROW STAYS, MARKED RETIRED. The first draft removed it — which also removed the outcome
      // that names what was discarded, so the record of the loss was never visible. Keeping the row
      // with no button is what makes "the loss is on the record" true on screen, not only in the DB.
      // ⛔ BOTH totals are RECOMPUTED from the rows still pending, never decremented in place (codex,
      // round 10): decrementing `deletable` alone left `personasToDiscard` frozen, so retiring the only
      // eligible row read "0 deletable (3 persona(s) would be discarded)" — completed loss shown as
      // pending loss.
      setPreview((p) => {
        if (!p) return p;
        const rows = p.rows.map((r) => (r.category_path === row.category_path ? { ...r, retired: true } : r));
        const pending = rows.filter((r) => r.verdict === 'DELETABLE' && !r.retired);
        return { ...p, rows, deletable: pending.length, personasToDiscard: pending.reduce((n, r) => n + r.discarded_personas.length, 0) };
      });
      onRetired?.();
    } catch (e: any) {
      if (generationRef.current === gen) {
        setOutcome((o) => ({ ...o, [row.category_path]: { ok: false, message: e?.message || String(e) } }));
      }
    } finally {
      setDeleting(null);
    }
  }

  const verdictBadge = (v: LeafVerdict) => {
    if (v === 'DELETABLE') return <Badge variant="secondary">Deletable</Badge>;
    if (v === 'REFUSED') return <Badge variant="destructive">Refused</Badge>;
    return <Badge variant="outline">No full-path twin — not authorised</Badge>;
  };

  return (
    <Card className="mb-4" data-testid="leaf-retirement-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Scissors className="h-4 w-4" />
          Retire leaf duplicates
        </CardTitle>
        <CardDescription>
          A bare-leaf row (<span className="font-mono">Backpacks</span>) shadows its full path
          (<span className="font-mono">Luggage &amp; Bags &gt; Backpacks</span>): research finds the leaf row,
          treats it as cached, and researches the leaf again. Full-path rows are canonical (CEO, 2026-09-08).
          Retiring a leaf merges its non-persona research into the full-path row and then deletes it;
          <strong> personas that exist only on the leaf are discarded</strong> — accepted loss, listed per row and
          recorded on the surviving row. Where both rows hold an object field with different content,
          <strong> the full path's value stands and the leaf's is dropped</strong> (CEO, 2026-09-09) — also listed per row,
          and recorded with the dropped value on the surviving row. A leaf with no full-path twin is never deleted here. One row per click.
          There is no bulk retire.
        </CardDescription>
        {/* ⛔ THE CONCURRENCY CONTRACT, STATED TO THE OPERATOR (orchestrator, 2026-09-08). Thirteen review
            rounds found four interleavings between retirement and other writers; each found one is handled,
            and the class is not exhausted. This is a destructive maintenance tool: it is safe when nothing
            else is writing the sector, and that precondition is the operator's to honour, so it is said here
            rather than assumed. */}
        <Alert className="mt-3" data-testid="leaf-retirement-contract">
          <ShieldAlert className="h-4 w-4" />
          <AlertDescription>
            <strong>Run this only when nothing else is writing this sector.</strong> Retirement is safe when no
            category research, gap fill or persona reconciliation is in flight for the sector. A row being
            researched is refused, and writers that lose the row mid-flight re-point to the full path — but the
            tool assumes no concurrent writer for the sector being retired, and that is your call to honour.
          </AlertDescription>
        </Alert>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-3">
          <Button size="sm" onClick={runPreview} disabled={loading || deleting !== null || sectorId === null} data-testid="leaf-retirement-preview">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Preview leaf rows
          </Button>
          {inScope && preview ? (
            <span className="text-sm text-muted-foreground" data-testid="leaf-retirement-counts">
              {preview.rows.length} leaf row(s) · {preview.deletable} deletable ({preview.personasToDiscard} persona(s) would be discarded) ·{' '}
              {preview.refused} refused · {preview.noTwin} without a full-path twin
            </span>
          ) : null}
        </div>

        {error ? (
          <Alert variant="destructive" data-testid="leaf-retirement-error">
            <ShieldAlert className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {preview !== null && !inScope ? (
          <Alert data-testid="leaf-retirement-stale">
            <AlertDescription>
              The sector changed since this preview was taken, so its verdicts no longer describe what is on
              screen. Run Preview again.
            </AlertDescription>
          </Alert>
        ) : null}

        {inScope && preview && preview.rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No bare-leaf rows in this sector.</p>
        ) : null}

        {inScope && preview && preview.rows.length > 0 ? (
          <div className="border rounded-md divide-y">
            {preview.rows.map((row) => {
              const out = outcome[row.category_path];
              return (
                <div key={row.category_path} className="p-3 flex items-start gap-3" data-testid={`leaf-row-${row.category_path}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm">{row.category_path}</span>
                      {verdictBadge(row.verdict)}
                      <span className="text-xs text-muted-foreground">
                        {row.times_used ?? 0} reads · {row.personas} persona(s), {row.weighted_personas} weighted
                      </span>
                    </div>
                    {/* ⛔ THE PREVIEW'S LOSS DESCRIPTION LEAVES WITH THE BUTTON (codex round 3, both legs, :303).
                        `because` is built from the PREVIEW and names the predicted discards and dropped fields; the
                        outcome below is built from the DELETE RESPONSE. Once the row is retired, showing both puts two
                        loss statements on one row with nothing marking which is the record — the outcome is the only
                        statement a retired row makes. The other three preview-derived lines already carry this guard. */}
                    {!row.retired ? <p className="text-xs text-muted-foreground mt-1" data-testid="leaf-row-because">{row.because}</p> : null}
                    {row.full_path ? (
                      <p className="text-xs mt-1">
                        Full path: <span className="font-mono">{row.full_path}</span> —{' '}
                        {row.full_path_personas} persona(s), {row.full_path_weighted} weighted
                      </p>
                    ) : null}
                    {!row.retired && row.verdict === 'DELETABLE' && row.discarded_personas.length > 0 ? (
                      <p className="text-xs mt-1 text-amber-700" data-testid="leaf-row-discards">
                        Will discard {row.discarded_personas.length} persona(s):{' '}
                        {row.discarded_personas.map((d) => `${d.persona_key} (${d.reason.replace(/_/g, ' ')})`).join(', ')}
                      </p>
                    ) : null}
                    {!row.retired && row.verdict === 'DELETABLE' && row.merge && row.merge.fields.length > 0 ? (
                      <p className="text-xs mt-1" data-testid="leaf-row-merge">
                        Will merge into the full-path row first: {row.merge.fields.join(', ')}
                      </p>
                    ) : null}
                    {!row.retired && row.verdict === 'DELETABLE' && row.merge && (row.merge.discarded_fields?.length ?? 0) > 0 ? (
                      <p className="text-xs mt-1 text-amber-700" data-testid="leaf-row-conflicts">
                        Full path wins on {row.merge.discarded_fields!.length} field(s) both rows hold differently — the leaf's version is dropped
                        (CEO ruling 2026-09-09): {row.merge.discarded_fields!.join(', ')}
                      </p>
                    ) : null}
                    {out ? (
                      <p className={`text-xs mt-1 ${out.ok ? 'text-green-700' : 'text-destructive'}`} data-testid="leaf-row-outcome">
                        {out.message}
                      </p>
                    ) : null}
                  </div>
                  {row.retired ? (
                    <Badge variant="secondary" data-testid="leaf-row-retired">Retired</Badge>
                  ) : row.verdict === 'DELETABLE' ? (
                    confirming === row.category_path ? (
                      <div className="flex items-center gap-2 shrink-0">
                        <Button size="sm" variant="destructive" onClick={() => retire(row)} disabled={deleting !== null} data-testid="leaf-row-confirm">
                          {deleting === row.category_path ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                          {[
                            'Delete permanently',
                            row.discarded_personas.length > 0 ? `discards ${row.discarded_personas.length} persona(s)` : null,
                            (row.merge?.discarded_fields?.length ?? 0) > 0 ? `drops ${row.merge!.discarded_fields!.length} leaf field(s)` : null,
                          ].filter(Boolean).join(' — ')}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => setConfirming(row.category_path)}
                        disabled={deleting !== null}
                        data-testid="leaf-row-retire"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Retire
                      </Button>
                    )
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
